//frontend/src/pages/company/Products.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import CompanyLayout from '../../components/layout/CompanyLayout';
import { productAPI, categoryAPI, shopAPI } from '../../services/api';

const Products = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('products');
  
  // Products state
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [shopFilter, setShopFilter] = useState(searchParams.get('shop_id') || '');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Categories state
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryFormData, setCategoryFormData] = useState({
    name: '',
    parent_id: null,
    sort_order: 0
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20,
    hasMore: false
  });

  const [productFormData, setProductFormData] = useState({
    product_name: '',
    category_id: '',
    shop_id: '',
    has_variants: false,
    base_price: '',
    barcode: '',
    description: ''
  });

  useEffect(() => {
    if (activeTab === 'products') {
      fetchProducts();
      fetchShops();
    }
    fetchCategories();
  }, [searchTerm, shopFilter, categoryFilter, currentPage, activeTab]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20
      };
      
      if (searchTerm) params.search = searchTerm;
      if (shopFilter) params.shop_id = shopFilter;
      if (categoryFilter) params.category_id = categoryFilter;
      
      const response = await productAPI.getAll(params);
      const data = response.data;
      
      setProducts(data.data || []);
      
      // Set pagination info
      if (data.pagination) {
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Fetch products error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getTree();
      setCategories(response.data.data || []);
    } catch (error) {
      console.error('Fetch categories error:', error);
    }
  };

  const fetchShops = async () => {
    try {
      const response = await shopAPI.getAll();
      setShops(response.data.data || []);
    } catch (error) {
      console.error('Fetch shops error:', error);
    }
  };

  // ============================================
  // PRODUCT HANDLERS
  // ============================================
  const handleCreateProduct = () => {
    setEditingProduct(null);
    setProductFormData({
      product_name: '',
      category_id: '',
      shop_id: shopFilter || '',
      has_variants: false,
      base_price: '',
      barcode: '',
      description: ''
    });
    setShowProductModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductFormData({
      product_name: product.product_name,
      category_id: product.category_id || '',
      shop_id: product.shop_id,
      has_variants: product.has_variants,
      base_price: product.base_price || '',
      barcode: product.barcode || '',
      description: product.description || ''
    });
    setShowProductModal(true);
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    try {
      const selectedShop = shops.find(s => s.id === parseInt(productFormData.shop_id));
      
      if (!selectedShop) {
        alert('Онлайн шоп сонгоно уу');
        return;
      }

      if (!selectedShop.warehouse_id) {
        alert('Энэ шопод агуулах холбогдоогүй байна.');
        return;
      }

      const submitData = {
        ...productFormData,
        warehouse_id: selectedShop.warehouse_id,
        category_id: productFormData.category_id ? parseInt(productFormData.category_id) : null,
        shop_id: parseInt(productFormData.shop_id),
        base_price: parseFloat(productFormData.base_price) || null,
      };

      if (editingProduct) {
        await productAPI.update(editingProduct.id, submitData);
        alert('Бараа амжилттай шинэчлэгдлээ');
      } else {
        await productAPI.create(submitData);
        alert('Бараа амжилттай үүслээ');
      }
      setShowProductModal(false);
      fetchProducts();
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!confirm('Энэ барааг устгахдаа итгэлтэй байна уу?')) return;
    try {
      await productAPI.delete(id);
      alert('Бараа амжилттай устлаа');
      fetchProducts();
    } catch (error) {
      alert(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  // ============================================
  // CATEGORY HANDLERS
  // ============================================
  const handleCreateCategory = (parentId = null) => {
    setEditingCategory(null);
    setCategoryFormData({
      name: '',
      parent_id: parentId,
      sort_order: 0
    });
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name,
      parent_id: category.parent_id,
      sort_order: category.sort_order || 0
    });
    setShowCategoryModal(true);
  };

  const handleSubmitCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await categoryAPI.update(editingCategory.id, categoryFormData);
        alert('Ангилал амжилттай шинэчлэгдлээ');
      } else {
        await categoryAPI.create(categoryFormData);
        alert('Ангилал амжилттай үүслээ');
      }
      setShowCategoryModal(false);
      fetchCategories();
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!confirm('Энэ ангиллыг устгахдаа итгэлтэй байна уу?')) return;
    try {
      await categoryAPI.delete(id);
      alert('Ангилал амжилттай устлаа');
      fetchCategories();
    } catch (error) {
      alert(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  const renderCategoryTree = (categories, level = 0) => {
    return categories.map((category) => (
      <div key={category.id} style={{ marginLeft: `${level * 20}px` }} className="border-b border-gray-200">
        <div className="flex items-center justify-between p-4 hover:bg-gray-50">
          <div className="flex items-center gap-3">
            <span className="text-2xl">{level === 0 ? '📁' : '📄'}</span>
            <div>
              <div className="font-medium text-gray-900">{category.name}</div>
              <div className="text-xs text-gray-500">
                Эрэмбэ: {category.sort_order} | Бараа: {category.product_count || 0}
                {level === 0 && <span className="ml-2 text-blue-600">(Үндсэн ангилал)</span>}
                {level === 1 && <span className="ml-2 text-purple-600">(Дэд ангилал)</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {level === 0 && hasPermission('product.create') && (
              <button
                onClick={() => handleCreateCategory(category.id)}
                className="text-sm px-3 py-1 text-blue-600 hover:bg-blue-50 rounded"
                title="Дэд ангилал нэмэх"
              >
                + Дэд
              </button>
            )}
            {hasPermission('product.edit') && (
            <button
              onClick={() => handleEditCategory(category)}
              className="text-sm px-3 py-1 text-gray-600 hover:bg-gray-50 rounded"
            >
              Засах
            </button>
            )}
            {hasPermission('product.delete') && (
            <button
              onClick={() => handleDeleteCategory(category.id)}
              className="text-sm px-3 py-1 text-red-600 hover:bg-red-50 rounded"
            >
              Устгах
            </button>
            )}
          </div>
        </div>
        {level === 0 && category.children && category.children.length > 0 && renderCategoryTree(category.children, level + 1)}
      </div>
    ));
  };

  // ============================================
  // PAGINATION
  // ============================================
  const handleShopFilterChange = (value) => {
    setShopFilter(value);
    setCurrentPage(1);
    if (value) {
      setSearchParams({ shop_id: value });
    } else {
      setSearchParams({});
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    const showPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(pagination.totalPages, startPage + showPages - 1);

    if (endPage - startPage < showPages - 1) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center justify-between px-6 py-4 bg-white border-t border-gray-200">
        <div className="text-sm text-gray-700">
          <span className="font-medium">{((currentPage - 1) * (pagination.itemsPerPage || 20)) + 1}</span>
          {' - '}
          <span className="font-medium">
            {Math.min(currentPage * (pagination.itemsPerPage || 20), pagination.totalItems || 0)}
          </span>
          {' / '}
          <span className="font-medium">{pagination.totalItems || 0}</span>
          {' бараа'}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
              currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            ← Өмнөх
          </button>

          {startPage > 1 && (
            <>
              <button
                onClick={() => handlePageChange(1)}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                1
              </button>
              {startPage > 2 && <span className="text-gray-500">...</span>}
            </>
          )}

          {pages.map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                currentPage === page
                  ? 'bg-primary-600 text-white'
                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {page}
            </button>
          ))}

          {endPage < pagination.totalPages && (
            <>
              {endPage < pagination.totalPages - 1 && <span className="text-gray-500">...</span>}
              <button
                onClick={() => handlePageChange(pagination.totalPages)}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {pagination.totalPages}
              </button>
            </>
          )}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === pagination.totalPages}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
              currentPage === pagination.totalPages
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Дараах →
          </button>
        </div>
      </div>
    );
  };

  if (loading && products.length === 0 && activeTab === 'products') {
    return (
      <CompanyLayout>
        <div className="flex items-center justify-center h-64">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <div className="space-y-6">
        {/* TABS NAVIGATION */}
        <div className="bg-white rounded-xl border border-gray-200 p-1 inline-flex">
          <button
            onClick={() => setActiveTab('products')}
            className={`px-6 py-2.5 rounded-lg font-medium text-sm transition ${
              activeTab === 'products'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            📦 Бараа
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`px-6 py-2.5 rounded-lg font-medium text-sm transition ${
              activeTab === 'categories'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            📂 Ангилал
          </button>
        </div>

        {/* ============================================ */}
        {/* PRODUCTS TAB */}
        {/* ============================================ */}
        {activeTab === 'products' && (
          <>
            {/* ACTIONS BAR */}
              <div className="flex items-center justify-end">
                {hasPermission('product.create') && (
                <button 
                  onClick={handleCreateProduct} 
                  className="btn btn-primary flex items-center gap-2"
                >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                    <span>Бараа нэмэх</span>
                  </button>
                  )}
                </div>

            {/* FILTERS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white p-4 rounded-xl border border-gray-200">
              <div className="relative">
                <input 
                  type="text" 
                  placeholder="Бараа хайх..." 
                  value={searchTerm} 
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }} 
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
                />
                <svg className="absolute left-3 top-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              <select 
                value={shopFilter} 
                onChange={(e) => handleShopFilterChange(e.target.value)} 
                className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Бүх шоп</option>
                {shops.map((shop) => (
                  <option key={shop.id} value={shop.id}>
                    {shop.shop_name}
                  </option>
                ))}
              </select>

              <select 
                value={categoryFilter} 
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setCurrentPage(1);
                }} 
                className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Бүх ангилал</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* EMPTY STATE */}
            {products.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                <div className="text-6xl mb-4">📦</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Бараа байхгүй байна</h3>
                <p className="text-gray-600 mb-6">
                  {shopFilter ? 'Энэ шопод бараа байхгүй байна' : 'Эхний бараагаа нэмээд эхлээрэй'}
                </p>
                {hasPermission('product.create') && (
                  <button onClick={handleCreateProduct} className="btn btn-primary">+ Бараа нэмэх</button>
                )}
              </div>
            ) : (
              /* TABLE */
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Нэр</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ангилал</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Шоп</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Агуулах</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Үнэ</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Үлдэгдэл</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Үйлдэл</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {products.map((product) => (
                        <tr key={product.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {product.sku}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="flex items-center gap-2">
                              <span>{product.product_name}</span>
                              {product.has_variants && (
                                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">Вариант</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {product.category?.name || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {product.shop?.shop_name || '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {product.warehouse?.warehouse_name || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {product.base_price ? `${product.base_price.toLocaleString()}₮` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                            {product.stock_quantity || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-2">
                              {product.has_variants && (
                                <button
                                  onClick={() => navigate(`/company/products/${product.id}/variants`)}
                                  className="text-purple-600 hover:text-purple-800 p-1 hover:bg-purple-50 rounded transition"
                                  title="Варианууд"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                                  </svg>
                                </button>
                              )}
                              
                              {hasPermission('product.edit') && (
                              <button
                                onClick={() => handleEditProduct(product)}
                                className="text-gray-600 hover:text-gray-800 p-1 hover:bg-gray-50 rounded transition"
                                title="Засах"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                 </svg>
                                  </button>
                                )}

                                {hasPermission('product.delete') && (
                                <button
                                  onClick={() => handleDeleteProduct(product.id)}
                                  className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded transition"
                                  title="Устгах"
                                >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                          )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* PAGINATION */}
                {renderPagination()}
              </div>
            )}
          </>
        )}

        {/* ============================================ */}
        {/* CATEGORIES TAB */}
        {/* ============================================ */}
        {activeTab === 'categories' && (
          <>
           {/* ACTIONS BAR */}
            <div className="flex items-center justify-end">
              {hasPermission('product.create') && (
              <button 
                onClick={() => handleCreateCategory()} 
                className="btn btn-primary flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Ангилал нэмэх</span>
                </button>
                )}
              </div>

            {/* CATEGORIES TREE */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {categories.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📂</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Ангилал байхгүй байна</h3>
                  <p className="text-gray-600 mb-6">Эхний ангиллаа нэмээд эхлээрэй</p>
                  <button onClick={() => handleCreateCategory()} className="btn btn-primary">+ Ангилал нэмэх</button>
                </div>
              ) : (
                renderCategoryTree(categories)
              )}
            </div>
          </>
        )}
      </div>

      {/* ============================================ */}
      {/* PRODUCT MODAL */}
      {/* ============================================ */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="text-3xl">📦</span>
              <span>{editingProduct ? 'Бараа засах' : 'Бараа нэмэх'}</span>
            </h2>
            
            <form onSubmit={handleSubmitProduct} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Барааны нэр *
                  </label>
                  <input 
                    type="text" 
                    value={productFormData.product_name} 
                    onChange={(e) => setProductFormData({ ...productFormData, product_name: e.target.value })} 
                    className="admin-input" 
                    required 
                    placeholder="Жнь: Samsung Galaxy S25" 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ангилал
                  </label>
                  <select 
                    value={productFormData.category_id} 
                    onChange={(e) => setProductFormData({ ...productFormData, category_id: e.target.value })} 
                    className="admin-input"
                  >
                    <option value="">-- Ангилал сонгох --</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Онлайн шоп *
                  </label>
                  <select 
                    value={productFormData.shop_id} 
                    onChange={(e) => setProductFormData({ ...productFormData, shop_id: e.target.value })} 
                    className="admin-input"
                    required
                  >
                    <option value="">-- Шоп сонгох --</option>
                    {shops.map((shop) => (
                      <option key={shop.id} value={shop.id}>
                        {shop.shop_name} ({shop.warehouse?.warehouse_name || 'Агуулахгүй'})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Үндсэн үнэ
                  </label>
                  <input 
                    type="number" 
                    value={productFormData.base_price} 
                    onChange={(e) => setProductFormData({ ...productFormData, base_price: e.target.value })} 
                    className="admin-input" 
                    placeholder="0" 
                    step="0.01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Баркод
                  </label>
                  <input 
                    type="text" 
                    value={productFormData.barcode} 
                    onChange={(e) => setProductFormData({ ...productFormData, barcode: e.target.value })} 
                    className="admin-input" 
                    placeholder="123456789"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Тайлбар
                  </label>
                  <textarea 
                    value={productFormData.description} 
                    onChange={(e) => setProductFormData({ ...productFormData, description: e.target.value })} 
                    className="admin-input" 
                    rows="3" 
                    placeholder="Барааны тайлбар"
                  ></textarea>
                </div>

                <div className="col-span-2">
                  <label className="flex items-center cursor-pointer p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <input 
                      type="checkbox" 
                      checked={productFormData.has_variants} 
                      onChange={(e) => setProductFormData({ ...productFormData, has_variants: e.target.checked })} 
                      className="mr-3" 
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-900">Вариант ашиглах</span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Өнгө, хэмжээ, багтаамж зэрэг сонголттой бол тэмдэглэнэ
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button 
                  type="button" 
                  onClick={() => setShowProductModal(false)} 
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  Болих
                </button>
                <button 
                  type="submit" 
                  className="flex-1 btn btn-primary"
                >
                  ✅ Хадгалах
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* CATEGORY MODAL */}
      {/* ============================================ */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="text-3xl">📂</span>
              <span>{editingCategory ? 'Ангилал засах' : 'Ангилал нэмэх'}</span>
            </h2>
            
            <form onSubmit={handleSubmitCategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ангиллын нэр *
                </label>
                <input 
                  type="text" 
                  value={categoryFormData.name} 
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, name: e.target.value })} 
                  className="admin-input" 
                  required 
                  placeholder="Жнь: Электроникс" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Эрэмбэ
                </label>
                <input 
                  type="number" 
                  value={categoryFormData.sort_order} 
                  onChange={(e) => setCategoryFormData({ ...categoryFormData, sort_order: parseInt(e.target.value) || 0 })} 
                  className="admin-input" 
                  placeholder="0" 
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button 
                  type="button" 
                  onClick={() => setShowCategoryModal(false)} 
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  Болих
                </button>
                <button 
                  type="submit" 
                  className="flex-1 btn btn-primary"
                >
                  ✅ Хадгалах
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CompanyLayout>
  );
};

export default Products;